/* ==========================================================================
   SEDA FASHION — comportamiento del storefront (vanilla, sin dependencias)
   - Carrito: Ajax API de Shopify (/cart.js, /cart/add.js, /cart/change.js)
   - Drawers: menú mobile y carrito
   - Carruseles: galería de producto y hero
   - PDP: selector de pack, barra sticky mobile
   - Antes/Después, modal "Saber más", barra de anuncios
   ========================================================================== */
(() => {
  if (window.Seda) return;

  const root = window.Shopify?.routes?.root || '/';

  /* ---------- Utilidades ---------- */
  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    const [int, dec] = value.toFixed(2).split('.');
    const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return dec === '00' ? `$${intFmt}` : `$${intFmt},${dec}`;
  };

  const escapeHTML = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const lockScroll = (on) => document.documentElement.classList.toggle('seda-lock', on);

  /* ---------- Carrito ---------- */
  const Cart = {
    async get() {
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    },
    async add(items) {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'No pudimos agregar el producto.');
      const cart = await this.get();
      this.emit(cart, { open: true });
      return cart;
    },
    async change(line, quantity) {
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      const cart = await res.json();
      this.emit(cart, { open: false });
      return cart;
    },
    /* Cambia varias cantidades por ID de variante en una sola llamada (ej. pasar de 1 pack a 2 packs). */
    async update(updates) {
      const res = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || cart.message || 'No pudimos actualizar el carrito.');
      this.emit(cart, { open: false });
      return cart;
    },
    /* Datos del producto (variantes con compare_at_price) para calcular ahorro y upsell en el drawer. */
    products: new Map(),
    async product(handle) {
      if (!handle) return null;
      if (!this.products.has(handle)) {
        this.products.set(
          handle,
          fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        );
      }
      return this.products.get(handle);
    },
    emit(cart, { open }) {
      document.dispatchEvent(new CustomEvent('seda:cart:updated', { detail: { cart, open } }));
    },
  };

  /* Cualquier form con data-seda-add agrega al carrito por Ajax (PDP y cards). */
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-seda-add]')) return;
    event.preventDefault();

    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Number(data.get('quantity') || 1);
    const buttons = form.querySelectorAll('[type="submit"]');
    const external = form.id ? document.querySelectorAll(`[form="${form.id}"][type="submit"]`) : [];
    const all = [...buttons, ...external];
    const error = form.querySelector('[data-seda-error]');

    all.forEach((b) => {
      b.dataset.label = b.dataset.label || b.innerHTML;
      b.setAttribute('aria-disabled', 'true');
      b.disabled = true;
      const text = b.querySelector('[data-seda-btn-text]');
      if (text) text.textContent = 'Agregando…';
    });
    if (error) error.hidden = true;

    try {
      await Cart.add([{ id, quantity }]);
    } catch (e) {
      if (error) {
        error.textContent = e.message;
        error.hidden = false;
      }
    } finally {
      all.forEach((b) => {
        b.disabled = false;
        b.removeAttribute('aria-disabled');
        if (b.dataset.label) b.innerHTML = b.dataset.label;
        delete b.dataset.label;
      });
      document.dispatchEvent(new CustomEvent('seda:buttons:restored'));
    }
  });

  /* Contadores del carrito en el header */
  document.addEventListener('seda:cart:updated', (e) => {
    const count = e.detail.cart.item_count;
    document.querySelectorAll('[data-seda-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  });

  /* ---------- Drawers ---------- */
  class SedaDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.seda-drawer__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-seda-close]')) this.close();
      });
      this.onKey = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      lockScroll(false);
    }
    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      requestAnimationFrame(() => {
        const focusable = this.panel?.querySelector('button, a, input');
        focusable?.focus({ preventScroll: true });
      });
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      this.lastFocus?.focus?.({ preventScroll: true });
    }
  }
  if (!customElements.get('seda-drawer')) customElements.define('seda-drawer', SedaDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-seda-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.dataset.sedaOpen);
    if (!target?.open) return;
    e.preventDefault();
    target.open(trigger);
  });

  /* ---------- Drawer del carrito ---------- */
  class SedaCart extends SedaDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.threshold = Number(this.dataset.freeShipping || 0);
      this.installments = Number(this.dataset.installments || 0);
      this.emptyUrl = this.dataset.emptyUrl || '/collections/all';
      this.onUpdate = (e) => {
        this.render(e.detail.cart);
        if (e.detail.open) this.open();
      };
      document.addEventListener('seda:cart:updated', this.onUpdate);

      this.addEventListener('click', async (e) => {
        const upsell = e.target.closest('[data-seda-upsell-to]');
        if (upsell) {
          /* Upsell: reemplaza la línea de 1 pack por la variante de 2 packs (misma cantidad). */
          const from = upsell.dataset.sedaUpsellFrom;
          const to = upsell.dataset.sedaUpsellTo;
          const qty = Number(upsell.dataset.qty || 1);
          const already = Number(upsell.dataset.toQty || 0);
          this.classList.add('is-loading');
          try {
            await Cart.update({ [from]: 0, [to]: already + qty });
          } finally {
            this.classList.remove('is-loading');
          }
          return;
        }
        const btn = e.target.closest('[data-line]');
        if (!btn) return;
        const line = Number(btn.dataset.line);
        const qty = Number(btn.dataset.qty);
        this.classList.add('is-loading');
        try {
          await Cart.change(line, qty);
        } finally {
          this.classList.remove('is-loading');
        }
      });

      Cart.get().then((cart) => {
        this.render(cart);
        Cart.emit(cart, { open: new URLSearchParams(location.search).get('cart') === 'open' });
      });
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener('seda:cart:updated', this.onUpdate);
    }
    async render(cart) {
      /* Primero pinta con lo que hay; cuando llegan las variantes del producto, agrega ahorro y upsell. */
      this.paint(cart, new Map());
      const handles = [...new Set(cart.items.map((i) => i.handle).filter(Boolean))];
      if (!handles.length) return;
      const token = (this.renderToken = Symbol('render'));
      const entries = await Promise.all(handles.map(async (h) => [h, await Cart.product(h)]));
      if (this.renderToken !== token) return; // llegó un render más nuevo
      this.paint(cart, new Map(entries.filter(([, p]) => p)));
    }
    /* Ahorro de una variante contra su precio tachado, en centavos (0 si no hay). */
    saving(variant) {
      if (!variant || !variant.compare_at_price) return 0;
      return Math.max(0, Number(variant.compare_at_price) - Number(variant.price));
    }
    paint(cart, products) {
      const title = this.querySelector('[data-seda-cart-title]');
      if (title) title.textContent = cart.item_count ? `Tu carrito (${cart.item_count})` : 'Tu carrito';

      const ship = this.querySelector('[data-seda-cart-ship]');
      if (ship) {
        if (this.threshold > 0 && cart.item_count > 0) {
          const missing = this.threshold - cart.total_price;
          const pct = Math.min(100, Math.round((cart.total_price / this.threshold) * 100));
          ship.hidden = false;
          ship.querySelector('p').innerHTML =
            missing > 0
              ? `Te faltan <strong>${money(missing)}</strong> para el envío gratis.`
              : '<strong>¡Tenés envío gratis!</strong>';
          ship.querySelector('.seda-cart__bar').style.setProperty('--progress', `${pct}%`);
        } else {
          ship.hidden = true;
        }
      }

      const body = this.querySelector('[data-seda-cart-body]');
      const foot = this.querySelector('[data-seda-cart-foot]');
      const placeholder = this.querySelector('template[data-seda-thumb]')?.innerHTML || '';

      if (!cart.items.length) {
        body.innerHTML = `
          <div class="seda-cart__empty">
            <p class="seda-h3">Tu carrito está vacío</p>
            <p class="seda-body">Esta noche puede ser la primera.</p>
            <a class="seda-btn" href="${this.emptyUrl}">Ver la tienda</a>
          </div>`;
        foot.hidden = true;
        return;
      }

      /* Sin selector de cantidad: el pack se elige en la PDP. Solo "Quitar". */
      const upsells = [];
      body.innerHTML = `<ul class="seda-cart__items">${cart.items
        .map((item, i) => {
          const line = i + 1;
          const img = item.image
            ? `<img src="${item.image}${item.image.includes('?') ? '&' : '?'}width=200" alt="" width="80" height="80" loading="lazy">`
            : placeholder;
          const product = products.get(item.handle);
          const variants = product?.variants || [];
          const index = variants.findIndex((v) => Number(v.id) === Number(item.variant_id));
          const variant = variants[index];
          const saving = this.saving(variant) * item.quantity;
          const qtyText = item.quantity > 1 ? ` × ${item.quantity}` : '';

          /* Upsell: si la línea es el primer pack y existe un pack siguiente con precio tachado. */
          const next = index === 0 ? variants[1] : null;
          if (next && next.available !== false && this.saving(next) > 0) {
            const toQty = cart.items.find((x) => Number(x.variant_id) === Number(next.id))?.quantity || 0;
            upsells.push({ from: variant.id, to: next.id, qty: item.quantity, toQty, saving: this.saving(next) });
          }

          return `
          <li class="seda-cart__item">
            <a class="seda-cart__thumb" href="${item.url}" tabindex="-1" aria-hidden="true">${img}</a>
            <div>
              <a class="seda-cart__name" href="${item.url}">${escapeHTML(item.product_title)}</a>
              <p class="seda-cart__variant">${item.variant_title ? escapeHTML(item.variant_title) : ''}${escapeHTML(qtyText)}</p>
              ${saving > 0 ? `<p class="seda-cart__save">Ahorrás ${money(saving)}</p>` : ''}
            </div>
            <div>
              <p class="seda-cart__price">${money(item.final_line_price)}</p>
              <button type="button" class="seda-cart__remove" data-line="${line}" data-qty="0">Quitar</button>
            </div>
          </li>`;
        })
        .join('')}</ul>${this.upsellHTML(upsells[0])}`;

      foot.hidden = false;
      foot.querySelector('[data-seda-cart-total]').textContent = money(cart.total_price);
      const cuotas = foot.querySelector('[data-seda-cart-cuotas]');
      if (cuotas && this.installments > 1) {
        cuotas.textContent = `${this.installments} cuotas sin interés de ${money(Math.round(cart.total_price / this.installments / 100) * 100)}`;
      }
    }
    upsellHTML(upsell) {
      const tpl = this.querySelector('template[data-seda-upsell]');
      if (!upsell || !tpl?.dataset.text) return '';
      const text = tpl.dataset.text.replace('[AHORRO]', money(upsell.saving));
      return `
        <div class="seda-cart__upsell">
          <p>${escapeHTML(text)}</p>
          <button
            type="button"
            class="seda-cart__upsell-btn"
            data-seda-upsell-from="${upsell.from}"
            data-seda-upsell-to="${upsell.to}"
            data-qty="${upsell.qty}"
            data-to-qty="${upsell.toQty}"
          >${escapeHTML(tpl.dataset.button || 'Cambiar')} ${this.icon('arrow-right')}</button>
        </div>`;
    }
    icon(name) {
      return this.querySelector(`template[data-seda-icon="${name}"]`)?.innerHTML || '';
    }
  }
  if (!customElements.get('seda-cart')) customElements.define('seda-cart', SedaCart);

  /* ---------- Carrusel (galería de producto y hero) ---------- */
  class SedaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.seda-carousel__track');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.prev = this.querySelector('[data-seda-prev]');
      this.next = this.querySelector('[data-seda-next]');
      this.dots = [...this.querySelectorAll('[data-seda-dot]')];
      this.thumbs = [...this.querySelectorAll('[data-seda-thumb]')];
      this.index = 0;

      this.prev?.addEventListener('click', () => this.go(this.index - 1));
      this.next?.addEventListener('click', () => this.go(this.index + 1));
      [...this.dots, ...this.thumbs].forEach((el) =>
        el.addEventListener('click', () => this.go(Number(el.dataset.index)))
      );

      this.onScroll = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => {
          const w = this.track.clientWidth || 1;
          const i = Math.round(this.track.scrollLeft / w);
          this.setActive(Math.max(0, Math.min(this.slides.length - 1, i)));
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.setActive(0);

      const autoplay = Number(this.dataset.autoplay || 0);
      if (autoplay > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = setInterval(() => {
          if (!this.matches(':hover')) this.go((this.index + 1) % this.slides.length);
        }, autoplay * 1000);
      }
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.track?.removeEventListener('scroll', this.onScroll);
    }
    go(i) {
      const n = this.slides.length;
      const target = ((i % n) + n) % n;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
      this.setActive(target);
    }
    setActive(i) {
      this.index = i;
      this.dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
      if (this.prev && !this.hasAttribute('loop')) this.prev.disabled = i === 0;
      if (this.next && !this.hasAttribute('loop')) this.next.disabled = i === this.slides.length - 1;
      this.slides.forEach((s, k) => s.setAttribute('aria-hidden', String(k !== i)));
      const active = this.thumbs[i];
      if (active && active.parentElement?.scrollHeight > active.parentElement?.clientHeight) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
  }
  if (!customElements.get('seda-carousel')) customElements.define('seda-carousel', SedaCarousel);

  /* ---------- PDP: formulario y selector de pack ---------- */
  class SedaProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-seda-add]');
      this.installments = Number(this.dataset.installments || 0);
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[name="id"]')) this.update(e.target);
      });
      const checked = this.querySelector('input[name="id"]:checked');
      if (checked) this.update(checked, { replaceUrl: false });
    }
    update(input, { replaceUrl = true } = {}) {
      const price = Number(input.dataset.price);
      const compare = Number(input.dataset.compare || 0);
      const label = input.dataset.title || '';

      this.querySelectorAll('[data-seda-price]').forEach((el) => (el.textContent = money(price)));
      document.querySelectorAll('[data-seda-sticky-price]').forEach((el) => (el.textContent = `${label} · ${money(price)}`));
      this.querySelectorAll('[data-seda-compare]').forEach((el) => {
        el.textContent = compare > price ? money(compare) : '';
        el.hidden = !(compare > price);
      });
      this.querySelectorAll('[data-seda-save]').forEach((el) => {
        el.textContent = compare > price ? `Ahorrás ${money(compare - price)}` : '';
        el.hidden = !(compare > price);
      });
      this.querySelectorAll('[data-seda-cuotas]').forEach((el) => {
        /* Cuota redondeada al peso: $35.000 en 3 → $11.667 */
        el.textContent =
          this.installments > 1
            ? `${this.installments} cuotas sin interés de ${money(Math.round(price / this.installments / 100) * 100)}`
            : '';
      });
      this.querySelectorAll('[data-seda-atc-price]').forEach((el) => (el.textContent = money(price)));
      const n = input.dataset.n || '1';
      this.querySelectorAll('[data-seda-btn-text][data-template]').forEach((el) => {
        /* 1 pack: texto simple. 2 o más: "Agregar [N] packs al carrito". */
        const multi = el.dataset.templateMulti;
        if (Number(n) > 1 && multi) el.textContent = multi.replace('[N]', n);
        else el.textContent = el.dataset.template.replace('[N]', n);
      });
      document.querySelectorAll('[data-seda-atc-price-ext]').forEach((el) => (el.textContent = money(price)));

      if (replaceUrl && input.value && this.hasAttribute('data-update-url')) {
        const url = new URL(location.href);
        url.searchParams.set('variant', input.value);
        history.replaceState({}, '', url);
      }
    }
  }
  if (!customElements.get('seda-product')) customElements.define('seda-product', SedaProduct);

  /* ---------- PDP: barra sticky mobile ---------- */
  class SedaSticky extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', show);
          document.body.classList.toggle('has-sticky-atc', show && matchMedia('(max-width: 989px)').matches);
        },
        { threshold: 0 }
      );
      this.io.observe(target);
    }
    disconnectedCallback() {
      this.io?.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }
  }
  if (!customElements.get('seda-sticky')) customElements.define('seda-sticky', SedaSticky);

  /* ---------- Antes / Después ---------- */
  class SedaCompare extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      this.onInput = () => this.style.setProperty('--pos', `${this.range.value}%`);
      this.range.addEventListener('input', this.onInput);
      this.onInput();
    }
    disconnectedCallback() {
      this.range?.removeEventListener('input', this.onInput);
    }
  }
  if (!customElements.get('seda-compare')) customElements.define('seda-compare', SedaCompare);

  /* ---------- Spotlight: una card iluminada, el resto atenuado ---------- */
  class SedaSpotlight extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-spotlight-item]')];
      if (!this.items.length) return;
      this.onPointer = (e) => {
        const item = e.target.closest('[data-spotlight-item]');
        if (item) this.activate(item);
      };
      this.addEventListener('pointerover', this.onPointer);
      this.addEventListener('pointerdown', this.onPointer);
      this.addEventListener('focusin', this.onPointer);
    }
    disconnectedCallback() {
      this.removeEventListener('pointerover', this.onPointer);
      this.removeEventListener('pointerdown', this.onPointer);
      this.removeEventListener('focusin', this.onPointer);
    }
    activate(item) {
      this.items.forEach((el) => el.classList.toggle('is-active', el === item));
    }
  }
  if (!customElements.get('seda-spotlight')) customElements.define('seda-spotlight', SedaSpotlight);

  /* ---------- Modal (dialog nativo) ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-seda-modal]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.sedaModal);
      if (dialog?.showModal) {
        dialog.showModal();
        lockScroll(true);
        dialog.addEventListener(
          'close',
          () => {
            lockScroll(false);
            opener.focus();
          },
          { once: true }
        );
      }
      return;
    }
    if (e.target.closest('[data-seda-modal-close]')) {
      e.target.closest('dialog')?.close();
      return;
    }
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('seda-modal')) {
      e.target.close();
    }
  });

  /* ---------- Reseñas: resumen bajo el título, "Ver más" y lightbox ---------- */
  const initReviews = () => {
    const section = document.querySelector('[data-seda-reviews]');
    const slots = document.querySelectorAll('[data-seda-rating-slot]');
    const summary = section?.querySelector('template[data-seda-reviews-summary]');

    /* Resumen (estrellas + nota + cantidad) calculado en la sección de reseñas → debajo del título del producto. */
    if (summary) {
      slots.forEach((slot) => {
        slot.innerHTML = summary.innerHTML;
        slot.hidden = false;
      });
    } else {
      slots.forEach((slot) => {
        if (!slot.querySelector('.seda-stars')) slot.hidden = true;
      });
    }

    /* Scroll suave al tocar el resumen */
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-seda-rating-slot] a[href="#resenas"], .seda-stars[href="#resenas"]');
      if (!link) return;
      const target = document.getElementById('resenas');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      history.replaceState({}, '', '#resenas');
    });

    if (!section) return;

    /* "Ver más": muestra las reseñas ocultas */
    section.querySelector('[data-seda-reviews-more]')?.addEventListener('click', (e) => {
      section.querySelectorAll('[data-seda-review-extra]').forEach((li) => (li.hidden = false));
      e.currentTarget.closest('.seda-reviews__more')?.remove();
    });

    /* Lightbox para fotos de reseñas y UGC */
    const dialog = section.querySelector('[data-seda-lightbox-dialog]');
    if (dialog?.showModal) {
      section.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-seda-lightbox]');
        if (!btn) return;
        const img = dialog.querySelector('img');
        img.src = btn.dataset.sedaLightbox;
        img.alt = btn.querySelector('img')?.alt || '';
        dialog.showModal();
        lockScroll(true);
        dialog.addEventListener(
          'close',
          () => {
            lockScroll(false);
            img.removeAttribute('src');
            btn.focus({ preventScroll: true });
          },
          { once: true }
        );
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReviews);
  else initReviews();

  /* ---------- Barra de anuncios ---------- */
  class SedaAnnouncement extends HTMLElement {
    connectedCallback() {
      this.msgs = [...this.querySelectorAll('.seda-announcement__msg')];
      if (this.msgs.length < 2) return;
      this.i = 0;
      this.querySelector('[data-seda-prev]')?.addEventListener('click', () => this.show(this.i - 1, true));
      this.querySelector('[data-seda-next]')?.addEventListener('click', () => this.show(this.i + 1, true));
      this.start();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    start() {
      clearInterval(this.timer);
      const s = Number(this.dataset.speed || 5);
      this.timer = setInterval(() => this.show(this.i + 1), s * 1000);
    }
    show(i, manual) {
      const n = this.msgs.length;
      this.i = ((i % n) + n) % n;
      this.msgs.forEach((m, k) => {
        m.classList.toggle('is-active', k === this.i);
        m.setAttribute('aria-hidden', String(k !== this.i));
      });
      if (manual) this.start();
    }
  }
  if (!customElements.get('seda-announcement')) customElements.define('seda-announcement', SedaAnnouncement);

  window.Seda = { Cart, money };
})();
